/**
 * AutomaDoc Sign: HTTP shapes for the portal, the public API, the signer app
 * and the verify page (§7.1, §7.3, §7.5, §5.2). camelCase, as every other
 * route; the rows in model.ts are snake_case as stored.
 *
 * Four route families, four kinds of credential:
 *   /api/esign/*         portal (company frontend): the portal JWT; members see
 *                        their own envelopes, app admins the account's (`scope=account`)
 *   /api/v1/envelopes*   public API: a company key (amk_…), API_RATE_LIMITER and
 *                        API_COMPANY_RATE_LIMITER as for documents
 *   /api/signer/*        signer app (sign.automadoc.cc): `Authorization: Bearer <link token>`,
 *                        plus `X-Sign-Session` after an access code; SIGN_RATE_LIMITER per IP
 *   /api/verify          verify page: public, SIGN_RATE_LIMITER per IP
 * and the worker's callbacks under /api/esign/worker/* (workerProtocol.ts).
 *
 * The link token never appears in a URL the API serves: the signer app keeps it
 * in the page's fragment and sends it as a header, so no log, trace or Referer
 * can carry it. (The legacy /api/sign/:token routes stay as they are.)
 */

import type { EsignPageSize } from './geometry'
import type {
  EnvelopeDocumentSource,
  EnvelopeEventDetail,
  EnvelopeEventType,
  EnvelopeKind,
  EnvelopeOrigin,
  EnvelopePrepareState,
  EnvelopeRecipientReach,
  EnvelopeRecipientRole,
  EnvelopeRecipientStatus,
  EnvelopeRecipientVerify,
  EnvelopeSendProblem,
  EnvelopeStatus,
  EsignFieldOptions,
  EsignFieldOrigin,
  EsignFieldType,
  EsignSavedSignatureKind,
} from './model'
import type { EsignAnchorHit, EsignPrepareErrorCode, EsignPrepareWarning, EsignTagHit } from './workerProtocol'

// =============================================================================
// Routes
// =============================================================================

/** Every route, Hono style. `esignPath()` fills the params for a client. */
export const ESIGN_ROUTES = {
  // Portal (JWT)
  listEnvelopes: 'GET /api/esign/envelopes',
  createEnvelope: 'POST /api/esign/envelopes',
  getEnvelope: 'GET /api/esign/envelopes/:envelopeId',
  updateEnvelope: 'PATCH /api/esign/envelopes/:envelopeId',
  deleteDraft: 'DELETE /api/esign/envelopes/:envelopeId',
  uploadDocument: 'POST /api/esign/envelopes/:envelopeId/documents',
  addGeneratedDocument: 'POST /api/esign/envelopes/:envelopeId/documents/from-generation',
  removeDocument: 'DELETE /api/esign/envelopes/:envelopeId/documents/:documentId',
  documentPages: 'GET /api/esign/envelopes/:envelopeId/documents/:documentId/pages',
  putRecipients: 'PUT /api/esign/envelopes/:envelopeId/recipients',
  putFields: 'PUT /api/esign/envelopes/:envelopeId/fields',
  applyTags: 'POST /api/esign/envelopes/:envelopeId/fields/from-tags',
  sendEnvelope: 'POST /api/esign/envelopes/:envelopeId/send',
  selfSign: 'POST /api/esign/envelopes/:envelopeId/self-sign',
  voidEnvelope: 'POST /api/esign/envelopes/:envelopeId/void',
  remindRecipient: 'POST /api/esign/envelopes/:envelopeId/recipients/:recipientId/remind',
  resendLink: 'POST /api/esign/envelopes/:envelopeId/recipients/:recipientId/resend',
  envelopeEvents: 'GET /api/esign/envelopes/:envelopeId/events',
  downloadFile: 'GET /api/esign/envelopes/:envelopeId/download',
  deleteFiles: 'POST /api/esign/envelopes/:envelopeId/delete-files',
  saveAsLayout: 'POST /api/esign/envelopes/:envelopeId/save-layout',
  listLayouts: 'GET /api/esign/layouts',
  getLayout: 'GET /api/esign/layouts/:layoutId',
  updateLayout: 'PATCH /api/esign/layouts/:layoutId',
  deleteLayout: 'DELETE /api/esign/layouts/:layoutId',
  getSavedSignatures: 'GET /api/esign/saved-signatures',
  putSavedSignature: 'PUT /api/esign/saved-signatures/:kind',
  deleteSavedSignature: 'DELETE /api/esign/saved-signatures/:kind',

  // Public API (company key)
  apiCreateEnvelope: 'POST /api/v1/envelopes',
  apiListEnvelopes: 'GET /api/v1/envelopes',
  apiGetEnvelope: 'GET /api/v1/envelopes/:envelopeId',
  apiSignedDocument: 'GET /api/v1/envelopes/:envelopeId/documents/:documentId/signed',
  apiCertificate: 'GET /api/v1/envelopes/:envelopeId/certificate',
  apiCombined: 'GET /api/v1/envelopes/:envelopeId/combined',
  apiVoid: 'POST /api/v1/envelopes/:envelopeId/void',
  apiRemind: 'POST /api/v1/envelopes/:envelopeId/recipients/:recipientId/remind',

  // Signer app (link token in Authorization)
  signerSession: 'GET /api/signer/session',
  signerUnlock: 'POST /api/signer/unlock',
  signerConsent: 'POST /api/signer/consent',
  signerViewed: 'POST /api/signer/viewed',
  signerFinish: 'POST /api/signer/finish',
  signerDecline: 'POST /api/signer/decline',
  signerDownload: 'GET /api/signer/download',
  /** The prepared PDFs, to read with an assistive tool before signing (§7.3). After consent. */
  signerDocument: 'GET /api/signer/document',

  // Verify page (public)
  verify: 'POST /api/verify',

  // Short signed URLs (public; the signature is the credential): page images, thumbnails,
  // saved signatures and downloads. Every `imageUrl`, `thumbUrl` and `EsignDownload.url`
  // is one of these; nothing builds them but the API (`?e=<expires>&s=<hmac>`).
  esignFile: 'GET /api/esign-files/*',
  // "Report this email" (public; signed per envelope): GET shows a confirm page, POST files
  // the report, which pauses the sender's sending (`esign_send_blocked`) until reviewed.
  reportPage: 'GET /api/esign-report/:envelopeId',
  reportSubmit: 'POST /api/esign-report/:envelopeId',

  // Generation worker callbacks (Bearer WORKER_SECRET); see workerProtocol.ts
  workerSourceFile: 'GET /api/esign/worker/documents/:documentId/source',
  workerPrepareFile: 'PUT /api/esign/worker/documents/:documentId/files/*',
  workerPrepared: 'POST /api/esign/worker/documents/:documentId/prepared',
  workerEnvelopeFile: 'GET /api/esign/worker/envelopes/:envelopeId/inputs/*',
  workerFinalizeFile: 'PUT /api/esign/worker/envelopes/:envelopeId/files/*',
  workerFinalized: 'POST /api/esign/worker/envelopes/:envelopeId/finalized',
} as const

export type EsignRouteName = keyof typeof ESIGN_ROUTES

/**
 * The company portal's Sign section (a hash route) and one envelope in it. Emails, bot cards
 * and the website link here. Not `#/sign/…`: that is the legacy signer link `#/sign/<token>`
 * until phase 2, and `#/sign/envelopes/x` would read as the token "envelopes".
 */
export const ESIGN_PORTAL_HASH = '#/esign'

export function esignEnvelopeHash(envelopeId: string): string {
  return `${ESIGN_PORTAL_HASH}/${encodeURIComponent(envelopeId)}`
}

/** The path of a route with its params filled: esignPath('getEnvelope', { envelopeId }). */
export function esignPath(route: EsignRouteName, params: Record<string, string> = {}): string {
  const path = ESIGN_ROUTES[route].slice(ESIGN_ROUTES[route].indexOf(' ') + 1)
  return path.replace(/:([A-Za-z]+)/g, (_, name: string) => {
    const value = params[name]
    if (value === undefined) throw new Error(`esignPath(${route}): missing ${name}`)
    return encodeURIComponent(value)
  })
}

// =============================================================================
// Errors
// =============================================================================

/** Every code an esign route answers with. Existing codes are reused where they mean the same. */
export const ESIGN_ERROR_CODES = [
  'INVALID_REQUEST',
  'ENVELOPE_NOT_FOUND',
  'DOCUMENT_NOT_FOUND',
  'RECIPIENT_NOT_FOUND',
  'LAYOUT_NOT_FOUND',
  'ENVELOPE_NOT_DRAFT',
  'ENVELOPE_CLOSED',
  'ENVELOPE_FINISHING',
  'SEND_PROBLEMS',
  'FILE_TOO_LARGE',
  'ENVELOPE_TOO_LARGE',
  'TOO_MANY_DOCUMENTS',
  'TOO_MANY_PAGES',
  'UNSUPPORTED_FILE',
  'GENERATION_NOT_FOUND',
  'GENERATION_NOT_COMPLETED',
  'ESIGN_ALREADY_PENDING',
  'ESIGN_WORKER_UNSUPPORTED',
  'ESIGN_WORKER_UNAVAILABLE',
  /** This environment cannot send email; nothing was sent or charged. */
  'ESIGN_EMAIL_UNAVAILABLE',
  /** Public API: no key, or a key that does not open envelopes (the document API's codes). */
  'API_KEY_MISSING',
  'API_KEY_INVALID',
  'ESIGN_SEND_BLOCKED',
  'ESIGN_EMAIL_UNVERIFIED',
  'ESIGN_SEND_RATE_LIMITED',
  'ESIGN_RECIPIENT_CAP',
  'QUOTA_EXCEEDED',
  'LAYOUT_LIMIT',
  'NOT_ALLOWED',
  // Signer routes
  'SIGN_NOT_FOUND',
  'SIGN_CLOSED',
  'SIGN_NOT_YOUR_TURN',
  'SIGN_ACCESS_CODE_REQUIRED',
  'SIGN_WRONG_ACCESS_CODE',
  'SIGN_LOCKED',
  'SIGN_CONSENT_REQUIRED',
  'SIGN_FIELDS_MISSING',
  'SIGN_INVALID_VALUE',
  'SIGN_INVALID_SIGNATURE',
  'SIGN_ALREADY_SIGNED',
  'RATE_LIMITED',
  /** AutomaDoc Sign is switched off in this environment (SIGN_AVAILABLE): 404 on every route. */
  'SIGN_UNAVAILABLE',
] as const

export type EsignErrorCode = (typeof ESIGN_ERROR_CODES)[number]

export interface EsignErrorResponse {
  code: EsignErrorCode
  /** English, for logs and a developer; the UIs translate the code. */
  error: string
  /** SEND_PROBLEMS only. */
  problems?: EnvelopeSendProblem[]
  /** SIGN_FIELDS_MISSING / SIGN_INVALID_VALUE: the fields at fault. */
  fieldIds?: string[]
  /** SIGN_WRONG_ACCESS_CODE: tries left before the link locks. */
  attemptsLeft?: number
  /** Where in an API request: `recipients[1].email`. */
  path?: string
}

// =============================================================================
// What the sender sees (portal and API)
// =============================================================================

/** Where an envelope came from, for the list's Source filter. */
export type EnvelopeSourceFilter = 'upload' | 'generated' | 'api'

export interface EnvelopePerson {
  userId: string
  name: string | null
  email: string | null
}

export interface EnvelopeRecipientBrief {
  id: string
  name: string
  email: string | null
  role: EnvelopeRecipientRole
  status: EnvelopeRecipientStatus
}

export interface EnvelopeSummary {
  id: string
  kind: EnvelopeKind
  origin: EnvelopeOrigin
  title: string
  /** Derived as of the response (envelopeStatus()). */
  status: EnvelopeStatus
  /** All signed, the signed PDF being made: status still 'sent'. */
  finishing: boolean
  source: EnvelopeSourceFilter
  sender: EnvelopePerson
  recipients: EnvelopeRecipientBrief[]
  progress: { done: number; total: number }
  createdAt: number
  sentAt: number | null
  expiresAt: number | null
  completedAt: number | null
  lastActivityAt: number
  /**
   * A signature_requests row shown in the same list (§10, phase 1 side by side).
   * Its id is the request id; open it with the existing /api/signatures routes.
   */
  legacy: boolean
}

export interface EnvelopeDocumentSummary {
  id: string
  position: number
  name: string
  source: EnvelopeDocumentSource
  sourceGenerationId: string | null
  prepareState: EnvelopePrepareState
  prepareError: EsignPrepareErrorCode | null
  warnings: EsignPrepareWarning[]
  pageCount: number | null
  pages: EsignPageSize[] | null
  /** Tags found at prepare time; their roles are the recipient slots to fill. */
  tags: EsignTagHit[]
  originalSha256: string | null
  preparedSha256: string | null
  finalSha256: string | null
  deliveredSha256: string | null
}

export interface EnvelopeRecipientSummary extends EnvelopeRecipientBrief {
  slot: string | null
  routingOrder: number
  userId: string | null
  reach: EnvelopeRecipientReach
  verify: EnvelopeRecipientVerify
  hasAccessCode: boolean
  bounced: boolean
  locked: boolean
  sentAt: number | null
  viewedAt: number | null
  completedAt: number | null
  declineReason: string | null
}

export interface EnvelopeFieldSummary {
  id: string
  documentId: string
  recipientId: string
  type: EsignFieldType
  page: number
  x: number
  y: number
  w: number
  h: number
  required: boolean
  label: string | null
  options: EsignFieldOptions | null
  origin: EsignFieldOrigin
  filled: boolean
  /** Text values once the envelope is completed (never a picture key). */
  value: string | null
}

export interface EnvelopeDetail extends EnvelopeSummary {
  message: string | null
  locale: string | null
  timezone: string | null
  expiryDays: number
  reminderEveryDays: number | null
  reminderMax: number | null
  appendCertificate: boolean
  voidReason: string | null
  /** What paid for the send ('free', 'pack:<id>', 'seat', 'automation', 'pro', 'none'). */
  chargedTo: string | null
  documents: EnvelopeDocumentSummary[]
  recipients: EnvelopeRecipientSummary[]
  fields: EnvelopeFieldSummary[]
  auditHead: string | null
  /** False once the files were deleted; the audit trail outlives them. */
  filesAvailable: boolean
  /**
   * Everyone signed but the signed PDF could not be made after every retry: the envelope
   * stays `sent` + `finishing`, the signatures are kept, and an operator was alerted.
   */
  finalizeFailed: boolean
  /** Draft only: what still stops a send (envelopeSendProblems()). */
  problems: EnvelopeSendProblem[]
}

/** A recipient's link, shown to the sender once (reach 'link', or a generated access code). */
export interface EnvelopeRecipientLink {
  recipientId: string
  name: string
  url: string
  /** A generated access code, present only the one time it is shown. */
  accessCode?: string
}

export interface EnvelopeEventSummary {
  id: string
  event: EnvelopeEventType
  at: number
  recipientId: string | null
  recipientName: string | null
  ip: string | null
  userAgent: string | null
  detail: EnvelopeEventDetail | null
  docSha256: string | null
  hash: string
}

/** A signed, short-lived link to one file (`Content-Disposition: attachment`). */
export interface EsignDownload {
  url: string
  filename: string
  expiresAt: number
  sha256: string | null
}

// =============================================================================
// Portal requests (/api/esign/*)
// =============================================================================

export interface ListEnvelopesQuery {
  /** 'account' needs app_admin. Default 'mine'. */
  scope?: 'mine' | 'account'
  status?: EnvelopeStatus
  /** users.lark_user_id; with scope=account. */
  sender?: string
  source?: EnvelopeSourceFilter
  /** created_at range, Unix ms. */
  from?: number
  to?: number
  /** Title or recipient name/email contains. */
  q?: string
  cursor?: string
  /** Default 25, at most 100. */
  limit?: number
}

export interface ListEnvelopesResponse {
  envelopes: EnvelopeSummary[]
  nextCursor: string | null
}

export interface CreateEnvelopeBody {
  kind: EnvelopeKind
  title?: string
  message?: string
  /** Start from a saved layout: its documents, slots, fields and settings are copied. */
  layoutId?: string
  /** Start from a generated document (any source); its PDF is copied in. */
  generationId?: string
  locale?: string
  timezone?: string
}

export interface EnvelopeResponse {
  envelope: EnvelopeDetail
}

export interface UpdateEnvelopeBody {
  title?: string
  message?: string | null
  expiryDays?: number
  /** null turns reminders off. */
  reminderEveryDays?: number | null
  reminderMax?: number | null
  appendCertificate?: boolean
  locale?: string
  timezone?: string
}

/**
 * POST …/documents is multipart/form-data: `file` (PDF, DOCX, XLSX, PPTX, read by
 * its bytes) and optional `name`. Answers at once with prepareState 'pending';
 * poll getEnvelope (every 2 s is fine) until 'ready' or 'failed'.
 */
export interface DocumentResponse {
  document: EnvelopeDocumentSummary
}

export interface AddGeneratedDocumentBody {
  generationId: string
}

export interface DocumentPagesResponse {
  documentId: string
  /** Width in pixels of every `imageUrl` (ESIGN_PAGE_IMAGE_WIDTH); thumbs are ESIGN_THUMB_WIDTH. */
  imageWidth: number
  pages: Array<EsignPageSize & { n: number; imageUrl: string; thumbUrl: string }>
  expiresAt: number
}

export interface EnvelopeRecipientInput {
  /** Keep an existing recipient (its token and status); omit for a new one. */
  id?: string
  role: EnvelopeRecipientRole
  slot?: string | null
  routingOrder: number
  name: string
  email?: string | null
  /** reach 'member': an account member. */
  userId?: string | null
  reach: EnvelopeRecipientReach
  verify: EnvelopeRecipientVerify
  /** verify 'access_code': a code to set; null clears; omitted keeps the current one. */
  accessCode?: string | null
  /** verify 'access_code': generate one per recipient, shown once in the send response. */
  generateAccessCode?: boolean
}

/** Replaces the draft's recipients. Fields of a removed recipient are removed too. */
export interface PutRecipientsBody {
  recipients: EnvelopeRecipientInput[]
}

export interface RecipientsResponse {
  recipients: EnvelopeRecipientSummary[]
}

export interface EnvelopeFieldInput {
  id?: string
  documentId: string
  recipientId: string
  type: EsignFieldType
  page: number
  x: number
  y: number
  w: number
  h: number
  required?: boolean
  label?: string | null
  options?: EsignFieldOptions | null
  /** Default 'editor'. */
  origin?: EsignFieldOrigin
}

/** Replaces the draft's fields. */
export interface PutFieldsBody {
  fields: EnvelopeFieldInput[]
}

export interface FieldsResponse {
  fields: EnvelopeFieldSummary[]
}

/**
 * Turn every tag found into a field for the recipient its slot is given to
 * (fieldRectFromTag()). Replaces earlier tag-made fields; editor fields stay.
 */
export interface ApplyTagsBody {
  /** slot ("Client") → recipient id. */
  slots: Record<string, string>
}

export interface ApplyTagsResponse {
  fields: EnvelopeFieldSummary[]
  /** Slots found in the documents and not given to anyone. */
  unassignedSlots: string[]
}

export interface SendEnvelopeResponse {
  envelope: EnvelopeDetail
  links: EnvelopeRecipientLink[]
}

/** A signer's values, in the portal's Sign it myself and the signer app alike. */
export interface EsignFieldValueInput {
  fieldId: string
  /** text, name, company, email fields. */
  text?: string
  /** checkbox fields. */
  checked?: boolean
}

/**
 * POST …/self-sign is multipart/form-data: `payload` (JSON, this shape) and,
 * unless adoptedFrom is 'saved', `signature` and/or `initials` PNG parts for the
 * picture fields present. Costs nothing (§8). Finalizing is asynchronous: poll
 * getEnvelope until 'completed'.
 */
export interface SelfSignPayload {
  values: EsignFieldValueInput[]
  typedName: string
  adoptedFrom: 'draw' | 'type' | 'upload' | 'saved'
  /** The ticked "I agree to sign this document electronically" line; refused (SIGN_CONSENT_REQUIRED) unless true. */
  agree: true
  /** The version of that line shown, ESIGN_SELF_SIGN_AGREEMENT_VERSION; any other is refused. */
  agreementVersion: string
  /** Keep the adopted pictures as the user's saved signature and initials. */
  saveSignature?: boolean
  /** "Email a copy to…" once completed. */
  emailCopyTo?: string
}

export interface VoidEnvelopeBody {
  reason: string
}

export interface RemindRecipientResponse {
  remindedAt: number
  channel: 'email' | 'lark' | 'teams'
}

/** Re-issues the recipient's token (the old link stops working). */
export interface ResendLinkResponse {
  /** Present for reach 'link' (nothing is sent; the sender copies it). */
  link?: EnvelopeRecipientLink
  sentAt: number | null
}

export interface EnvelopeEventsResponse {
  events: EnvelopeEventSummary[]
  /** verifyAuditChain() on the stored rows. */
  chain: { ok: boolean; head: string | null }
}

export type EsignDownloadFile = 'original' | 'signed' | 'certificate' | 'combined'

/** GET …/download?file=signed&documentId=… (documentId for 'original' and 'signed'). */
export interface DownloadQuery {
  file: EsignDownloadFile
  documentId?: string
}

export interface SaveAsLayoutBody {
  name: string
}

/** A layout's documents: own copies of the prepared PDFs (esignR2Keys.layout*). */
export interface EnvelopeLayoutDocument {
  position: number
  name: string
  preparedSha256: string
  originalSha256: string | null
  pageCount: number
  pages: EsignPageSize[]
}

/** A recipient slot of a layout: a role name, no person. */
export interface EnvelopeLayoutSlot {
  slot: string
  role: EnvelopeRecipientRole
  routingOrder: number
  verify: EnvelopeRecipientVerify
}

export interface EnvelopeLayoutField {
  documentPosition: number
  slot: string
  type: EsignFieldType
  page: number
  x: number
  y: number
  w: number
  h: number
  required: boolean
  label: string | null
  options: EsignFieldOptions | null
}

export interface EnvelopeLayoutSettings {
  title?: string
  message?: string | null
  expiryDays?: number
  reminderEveryDays?: number | null
  reminderMax?: number | null
  appendCertificate?: boolean
}

export interface EnvelopeLayoutSummary {
  id: string
  name: string
  documentCount: number
  slots: EnvelopeLayoutSlot[]
  createdBy: EnvelopePerson
  createdAt: number
  updatedAt: number
}

export interface EnvelopeLayoutDetail extends EnvelopeLayoutSummary {
  documents: EnvelopeLayoutDocument[]
  fields: EnvelopeLayoutField[]
  settings: EnvelopeLayoutSettings
}

export interface ListLayoutsResponse {
  layouts: EnvelopeLayoutSummary[]
  /** ESIGN_FREE_MAX_LAYOUTS on Free; null when unlimited. */
  limit: number | null
}

export interface LayoutResponse {
  layout: EnvelopeLayoutDetail
}

export interface UpdateLayoutBody {
  name?: string
  settings?: EnvelopeLayoutSettings
}

export interface SavedSignaturesResponse {
  signature: { url: string; createdAt: number } | null
  initials: { url: string; createdAt: number } | null
}

/** PUT …/saved-signatures/:kind takes the PNG as the raw body (image/png). */
export type SavedSignatureKindParam = EsignSavedSignatureKind

// =============================================================================
// Public API (/api/v1/envelopes)
// =============================================================================

/** An API caller's field relative to text in the document (§4.3 point 3). */
export interface EsignAnchorSpec {
  /** Text to find (findAnchorMatches()); at most ESIGN_ANCHOR_MAX_LENGTH characters. */
  anchor: string
  /** 'all' (default) or the n-th occurrence across the envelope, 1-based. */
  occurrence?: 'all' | number
  /** Points from the anchor's top-left, displayed frame. Default 0. */
  offsetX?: number
  offsetY?: number
  /** Default: the field type's ESIGN_DEFAULT_FIELD_SIZE. */
  w?: number
  h?: number
  /** A recipient `key` of the same request. */
  recipient: string
  type: EsignFieldType
  required?: boolean
  label?: string
}

export interface ApiEnvelopeDocumentInput {
  name?: string
  /** One of: base64 file bytes (total ≤ 10 MB of JSON), a generated document, or a multipart part name. */
  base64?: string
  generationId?: string
  file?: string
}

export interface ApiEnvelopeRecipientInput {
  /** The caller's own reference ("client"), used by fields and anchors; unique in the request. */
  key: string
  role: EnvelopeRecipientRole
  name: string
  email?: string
  routingOrder?: number
  /** Default 'email'; 'link' returns signingUrl in the response instead of mailing. */
  reach?: 'email' | 'link'
  accessCode?: string
  /** The tag slot this recipient fills (with autoTags). Default: none. */
  slot?: string
}

export interface ApiEnvelopeFieldInput {
  /** 0-based index into documents. */
  document: number
  /** A recipient `key`. */
  recipient: string
  type: EsignFieldType
  page: number
  x: number
  y: number
  w: number
  h: number
  required?: boolean
  label?: string
  options?: EsignFieldOptions
}

/**
 * POST /api/v1/envelopes: application/json with this body, or multipart/form-data
 * with an `envelope` part holding it and one part per `documents[].file`.
 * Documents are prepared asynchronously; with `send: true` the envelope is sent
 * as soon as every document is ready and the fields are placed, else it stays a
 * draft. Read the outcome with GET /api/v1/envelopes/:id (`sendError`).
 */
export interface ApiCreateEnvelopeRequest {
  title: string
  message?: string
  locale?: string
  timezone?: string
  expiryDays?: number
  /** false turns reminders off. */
  reminders?: { everyDays: number; max: number } | false
  appendCertificate?: boolean
  documents: ApiEnvelopeDocumentInput[]
  recipients: ApiEnvelopeRecipientInput[]
  fields?: ApiEnvelopeFieldInput[]
  anchors?: EsignAnchorSpec[]
  /** Place fields from `[[…]]` tags, slot → recipient by `recipients[].slot`. */
  autoTags?: boolean
  send?: boolean
}

/** envelopes.pending_placement: what an API create left to do once documents are prepared. */
export interface EnvelopePendingPlacement {
  autoTags: boolean
  /** Recipient keys resolved to recipient ids at create time. */
  anchors: Array<Omit<EsignAnchorSpec, 'recipient'> & { recipientId: string }>
  /** Send once placed. */
  send: boolean
  /**
   * The worker's anchor hits per document id (EsignPrepareManifest.anchors), kept until
   * every document is prepared: an `occurrence` counts across the whole envelope.
   */
  anchorHits?: Record<string, EsignAnchorHit[]>
  /** Set when every document is ready and placement is being applied (a claim, so it runs once). */
  applyingAt?: number
  /** Sign it myself: "email a copy to…", kept until the signed file exists (SelfSignPayload.emailCopyTo). */
  emailCopyTo?: string
  /** Set when done: null on success, else why it could not send. */
  outcome?: { code: EsignErrorCode; problems?: EnvelopeSendProblem[] } | null
}

export interface ApiEnvelopeRecipient {
  id: string
  key: string | null
  role: EnvelopeRecipientRole
  name: string
  email: string | null
  status: EnvelopeRecipientStatus
  routingOrder: number
  viewedAt: number | null
  completedAt: number | null
  declineReason: string | null
  /** reach 'link' only, and only in the response that created or sent the envelope. */
  signingUrl?: string
  accessCode?: string
}

export interface ApiEnvelopeDocument {
  id: string
  name: string
  prepareState: EnvelopePrepareState
  prepareError: EsignPrepareErrorCode | null
  pageCount: number | null
  originalSha256: string | null
  preparedSha256: string | null
  finalSha256: string | null
  deliveredSha256: string | null
}

export interface ApiEnvelope {
  id: string
  status: EnvelopeStatus
  finishing: boolean
  /** See EnvelopeDetail.finalizeFailed. */
  finalizeFailed: boolean
  title: string
  createdAt: number
  sentAt: number | null
  expiresAt: number | null
  completedAt: number | null
  documents: ApiEnvelopeDocument[]
  recipients: ApiEnvelopeRecipient[]
  auditHead: string | null
  /** `send: true` could not send once documents were ready: why. */
  sendError: { code: EsignErrorCode; problems?: EnvelopeSendProblem[] } | null
}

export interface ApiListEnvelopesQuery {
  status?: EnvelopeStatus
  /** updated since, Unix ms. */
  since?: number
  cursor?: string
  limit?: number
}

export interface ApiListEnvelopesResponse {
  envelopes: ApiEnvelope[]
  nextCursor: string | null
}

export interface ApiVoidEnvelopeRequest {
  reason: string
}

// =============================================================================
// Signer app (/api/signer/*)
// =============================================================================

/**
 * What a link is, as of now. A closed envelope's link answers with its state
 * and nothing else (no title, no names).
 */
export type SignerLinkState =
  /** Needs the access code first (X-Sign-Session missing or expired). */
  | 'needs_code'
  | 'locked'
  /** Open and this recipient's turn. */
  | 'open'
  /** Open, waiting for recipients earlier in the routing order. */
  | 'waiting'
  /** This recipient has finished; the envelope is still out with others. */
  | 'done'
  | 'completed'
  | 'declined'
  | 'voided'
  | 'expired'

export interface SignerEnvelopeInfo {
  title: string
  message: string | null
  senderName: string | null
  senderEmail: string | null
  locale: string | null
  timezone: string | null
  expiresAt: number | null
}

export interface SignerRecipientInfo {
  name: string
  email: string | null
  role: EnvelopeRecipientRole
  status: EnvelopeRecipientStatus
  consentGiven: boolean
  /** Locale of the recipient when known; the app falls back to the browser's. */
  locale: string | null
}

export interface SignerDocument {
  id: string
  name: string
  imageWidth: number
  pages: Array<EsignPageSize & { n: number; imageUrl: string }>
}

export interface SignerField {
  id: string
  documentId: string
  page: number
  x: number
  y: number
  w: number
  h: number
  type: EsignFieldType
  required: boolean
  label: string | null
  options: EsignFieldOptions | null
  /** This recipient's to fill. Others' are drawn as overlays when filled. */
  mine: boolean
  /** mine: the starting value (name, email, company). */
  prefill: string | null
  /** Another recipient's filled field: text, or a picture (signature/initials). */
  overlay: { text: string } | { imageUrl: string } | { checked: true } | null
}

export interface SignerSessionResponse {
  state: SignerLinkState
  /** The disclosure the consent step shows (ESIGN_DISCLOSURE_VERSION). */
  disclosureVersion: string
  /** Absent for a closed envelope and for 'needs_code' / 'locked'. */
  envelope?: SignerEnvelopeInfo
  recipient?: SignerRecipientInfo
  /** Only after consent, in states 'open', 'waiting' and 'done'. */
  documents?: SignerDocument[]
  fields?: SignerField[]
  /** 'waiting': how many recipients are still ahead. */
  waitingFor?: number
  /** 'done': everyone has signed and the signed PDF is being made. */
  everyoneSigned?: boolean
}

export interface SignerUnlockBody {
  accessCode: string
}

/** Send `X-Sign-Session: <session>` on every later call. */
export interface SignerUnlockResponse {
  session: string
  expiresAt: number
}

export interface SignerConsentBody {
  disclosureVersion: string
  agree: true
}

export interface SignerViewedBody {
  documentId: string
}

/**
 * POST /api/signer/finish is multipart/form-data: `payload` (JSON, this shape),
 * plus `signature` and `initials` PNG parts when the recipient has those fields.
 * One conditional UPDATE records it; the server checks every required field.
 */
export interface SignerFinishPayload {
  /**
   * The signer's values (pinned with slice S):
   * - a signature or initials field the signer applied is listed as `{ fieldId }` alone; an
   *   unapplied optional one is left out, and only listed picture fields are stamped (a
   *   required one left out is SIGN_FIELDS_MISSING);
   * - every checkbox is sent with `checked: true | false`;
   * - text arrives cleaned with cleanEsignText() (the server cleans it again);
   * - `adoptedFrom` is 'type' when the recipient has no picture fields.
   */
  values: EsignFieldValueInput[]
  typedName: string
  adoptedFrom: 'draw' | 'type' | 'upload'
  /** The second gate, as today's `consent: true`. */
  consent: true
}

export interface SignerDeclineBody {
  reason: string
}

export interface SignerStateResponse {
  state: SignerLinkState
  /**
   * /finish only, with 'done': true when this signer was the last one, so the signed PDF is
   * now being made; false when others still have to sign. Absent from an older API.
   */
  everyoneSigned?: boolean
}

/**
 * GET /api/signer/document: "Download the PDF to read it" (§7.3), before completion, for
 * screen readers. The prepared PDFs as everyone sees them; after consent, while the link is
 * open, waiting or done. Attachment downloads, never rendered on our origin.
 */
export interface SignerDocumentResponse {
  files: Array<EsignDownload & { documentId: string }>
}

/** Only once the envelope is completed. */
export interface SignerDownloadResponse {
  files: Array<EsignDownload & { kind: 'signed' | 'certificate' | 'combined'; documentId: string | null }>
}

// =============================================================================
// Verify page (POST /api/verify)
// =============================================================================

/** The browser hashes the file itself; the file never leaves the device. */
export interface VerifyRequest {
  /** Lower-case hex SHA-256 of the whole file. */
  sha256: string
}

/**
 * Matches a completed envelope's delivered file (a document's delivered_sha256,
 * or the combined file). Nothing beyond these three facts is ever revealed.
 */
export type VerifyResponse =
  | { match: false }
  | { match: true; envelopeId: string; completedAt: number; signerCount: number }
